/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AllegatoBoDTO,
  GetTipiGruppiAllegatoParams,
  GetTipiGruppiAllegatoTipiAllegatoParams,
  GruppoAllegatoCreate,
  GruppoAllegatoDTO,
  WsProblema,
  WsRispostaPaginataListGruppoAllegatoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli di un Gruppo Allegati.
 * @summary Dettaglio Gruppo Allegati
 */
export const getTipiGruppiAllegatoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GruppoAllegatoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiGruppiAllegatoIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-gruppi-allegato/${id}`] as const;

export const getGetTipiGruppiAllegatoIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiGruppiAllegatoIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>
  > = ({ signal }) => getTipiGruppiAllegatoId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiGruppiAllegatoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>
>;
export type GetTipiGruppiAllegatoIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Gruppo Allegati
 */
export const useGetTipiGruppiAllegatoId = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegatoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiGruppiAllegatoIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il Gruppo Allegati.
 * @summary Aggiorna il Gruppo Allegati
 */
export const putTipiGruppiAllegatoId = (
  id: string,
  gruppoAllegatoCreate: GruppoAllegatoCreate,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<GruppoAllegatoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: gruppoAllegatoCreate,
    },
    options,
  );
};

export const getPutTipiGruppiAllegatoIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiGruppiAllegatoId>>,
    TError,
    { id: string; data: GruppoAllegatoCreate },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiGruppiAllegatoId>>,
  TError,
  { id: string; data: GruppoAllegatoCreate },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiGruppiAllegatoId>>,
    { id: string; data: GruppoAllegatoCreate }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiGruppiAllegatoId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiGruppiAllegatoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiGruppiAllegatoId>>
>;
export type PutTipiGruppiAllegatoIdMutationBody = GruppoAllegatoCreate;
export type PutTipiGruppiAllegatoIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Aggiorna il Gruppo Allegati
 */
export const usePutTipiGruppiAllegatoId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiGruppiAllegatoId>>,
    TError,
    { id: string; data: GruppoAllegatoCreate },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiGruppiAllegatoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Elimina un Gruppo Allegati.
 * @summary Elimina Gruppo Allegati
 */
export const deleteTipiGruppiAllegatoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiGruppiAllegatoIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiGruppiAllegatoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiGruppiAllegatoId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiGruppiAllegatoId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiGruppiAllegatoId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiGruppiAllegatoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiGruppiAllegatoId>>
>;

export type DeleteTipiGruppiAllegatoIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina Gruppo Allegati
 */
export const useDeleteTipiGruppiAllegatoId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiGruppiAllegatoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiGruppiAllegatoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna l'elenco dei Gruppi Allegati.
 * @summary Elenco Gruppi Allegati
 */
export const getTipiGruppiAllegato = (
  params?: GetTipiGruppiAllegatoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListGruppoAllegatoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiGruppiAllegatoQueryKey = (
  params?: GetTipiGruppiAllegatoParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-gruppi-allegato`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiGruppiAllegatoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegato>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiGruppiAllegatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiGruppiAllegato>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiGruppiAllegatoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiGruppiAllegato>>
  > = ({ signal }) => getTipiGruppiAllegato(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiGruppiAllegatoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiGruppiAllegato>>
>;
export type GetTipiGruppiAllegatoQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Gruppi Allegati
 */
export const useGetTipiGruppiAllegato = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegato>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipiGruppiAllegatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiGruppiAllegatoQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo Gruppo Allegati.
 * @summary Crea Gruppo Allegati
 */
export const postTipiGruppiAllegato = (
  gruppoAllegatoCreate: GruppoAllegatoCreate,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<GruppoAllegatoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: gruppoAllegatoCreate,
    },
    options,
  );
};

export const getPostTipiGruppiAllegatoMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiGruppiAllegato>>,
    TError,
    { data: GruppoAllegatoCreate },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiGruppiAllegato>>,
  TError,
  { data: GruppoAllegatoCreate },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiGruppiAllegato>>,
    { data: GruppoAllegatoCreate }
  > = props => {
    const { data } = props ?? {};

    return postTipiGruppiAllegato(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiGruppiAllegatoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiGruppiAllegato>>
>;
export type PostTipiGruppiAllegatoMutationBody = GruppoAllegatoCreate;
export type PostTipiGruppiAllegatoMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Gruppo Allegati
 */
export const usePostTipiGruppiAllegato = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiGruppiAllegato>>,
    TError,
    { data: GruppoAllegatoCreate },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiGruppiAllegatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna l'elenco dei Tipi Allegato.
 * @summary Elenco Tipi Allegato
 */
export const getTipiGruppiAllegatoTipiAllegato = (
  params: GetTipiGruppiAllegatoTipiAllegatoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AllegatoBoDTO[]>(
    {
      url: `/framework-cfg-procedimenti/tipi-gruppi-allegato/tipi-allegato`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiGruppiAllegatoTipiAllegatoQueryKey = (
  params: GetTipiGruppiAllegatoTipiAllegatoParams,
) =>
  [
    `/framework-cfg-procedimenti/tipi-gruppi-allegato/tipi-allegato`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiGruppiAllegatoTipiAllegatoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>,
  TError = ErrorType<unknown>,
>(
  params: GetTipiGruppiAllegatoTipiAllegatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiGruppiAllegatoTipiAllegatoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>
  > = ({ signal }) =>
    getTipiGruppiAllegatoTipiAllegato(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiGruppiAllegatoTipiAllegatoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>
>;
export type GetTipiGruppiAllegatoTipiAllegatoQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Tipi Allegato
 */
export const useGetTipiGruppiAllegatoTipiAllegato = <
  TData = Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>,
  TError = ErrorType<unknown>,
>(
  params: GetTipiGruppiAllegatoTipiAllegatoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiGruppiAllegatoTipiAllegato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiGruppiAllegatoTipiAllegatoQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
